import {
  hexToHSL,
  hslToCSS,
  shouldDisplayToastVersion,
  updateLastToastDisplay,
} from "./../helper/helper";
import { useQuery } from "@tanstack/react-query";
import { AxiosError, type AxiosResponse } from "axios";
import { useDispatch } from "react-redux";
import { systemActions } from "@/redux/system/system-slice";
import type { DataAwareResponse, SystemRole, SystemSettings, User } from "@/types/types";
import { msalInstance, graphRequest, graphConfig } from "@/authConfig";
import { axiosInstance } from "@/helper/axios-instance";
import errorNotifications, { compareBackendVersions } from "@/helper/helper";
import { useIsAuthenticated } from "@azure/msal-react";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";

let isError = false;

export function useUser() {
  const account = msalInstance?.getActiveAccount();
  const dispatch = useDispatch();
  const isAuthed = useIsAuthenticated();

  return useQuery({
    queryKey: ["user", account?.idToken, isAuthed],
    queryFn: async () => {
      const systemSettings = await axiosInstance({
        method: "GET",
        url:  `Security/SystemSettings`,
      })
        .then((response: AxiosResponse<DataAwareResponse<SystemSettings>>) => {
          if (response.data.hasError) {
            errorNotifications(response.data.validation.items);
            return;
          }

          return response.data.data;
        })
        .catch((e) => {
          if (e instanceof AxiosError) {
            isError = true;
            dispatch(systemActions.setError(`Failed fetching user data - ${e.message}`));
          }
        });

      if (isError || !systemSettings?.user) return;

      if (systemSettings?.colorScheme) {
        const { primaryColor, secondaryColor } = systemSettings.colorScheme;

        document.documentElement.style.setProperty("--primary-color", primaryColor);
        document.documentElement.style.setProperty("--secondary-color", secondaryColor);

        const primaryHSL = hexToHSL(primaryColor);
        const tertiaryHSL = { ...primaryHSL, l: Math.min(primaryHSL.l + 15, 100) }; // create tertiary color with brightness increase by 15% based on primary color
        const tertiaryColor = hslToCSS(tertiaryHSL.h, tertiaryHSL.s, tertiaryHSL.l);

        document.documentElement.style.setProperty("--tertiary-color", tertiaryColor);

        dispatch(systemActions.setColorScheme(systemSettings.colorScheme));
        dispatch(systemActions.setLogo(systemSettings.logo));
      }

      const backendCompatibilityStatus = compareBackendVersions(systemSettings.version);

      if (!backendCompatibilityStatus.canRun) {
        if (shouldDisplayToastVersion()) {
          toast.error(backendCompatibilityStatus.message, {
            toastId: "version-error",
            autoClose: false,
          });
          updateLastToastDisplay();
        }
        return;
      }

      if (backendCompatibilityStatus.warn) {
        if (shouldDisplayToastVersion()) {
          toast.warn(backendCompatibilityStatus.message, {
            toastId: "version-warn",
            autoClose: false,
          });
          updateLastToastDisplay();
        }
      }

      dispatch(systemActions.setVersion(systemSettings.version));

      const roles = (systemSettings?.user?.roles as SystemRole[]).map(
        (role: SystemRole) => role.name
      );

      const userData: User = {
        ...systemSettings.user,
        name: account?.name,
        email: account?.username,
        roles: roles,
        photo: "",
      };

      Sentry.setUser({
        email: systemSettings.user.email,
        id: systemSettings.user.id,
        username: systemSettings.user.username,
      });

      const tokenResp = await msalInstance.acquireTokenSilent({
        ...graphRequest,
        account: account!,
      });

      const headers = new Headers();
      headers.append("Authorization", `Bearer ${tokenResp?.accessToken}`);
      headers.append("Content-Type", "image/jpeg");
      headers.append("Accept", "image/jpeg");

      const options = {
        method: "GET",
        headers: headers,
      };

      const response = await fetch(graphConfig.graphMePhotoEndpoint, options);

      if (!response.ok) {
        console.error("HTTP error! status: ", response.status);
      } else {
        const data = await response.blob();
        if (data !== null) {
          window.URL = window.URL || window.webkitURL;
          userData.photo = window.URL.createObjectURL(data);
          dispatch(systemActions.setUser(userData));

          return userData;
        }
      }
      dispatch(systemActions.setUser(userData));

      return userData;
    },
  });
}
